// @flow
import React, { Component } from "react";
import classNames from "classnames";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { withStyles } from "@material-ui/core/styles";

import type { DictionaryType } from "../../../global/types/GeneralTypes";
import type { ButtonStylesClassesType } from "../../../global/styles/buttonStyles";
import type { IconStylesClassesType } from "../../../global/styles/iconStyles";
import type { TableStylesClassesType } from "../../../global/styles/generalStyles";
import type { EnrolmentInputModel } from "../../types/InputModels";
import { buttonStyles } from "../../../global/styles/buttonStyles";
import { iconStyles } from "../../../global/styles/iconStyles";
import { tableStyles } from "../../../global/styles/generalStyles";
import { Stringellipsis } from "../../../global/functions/StringOperations";
import { ConfirmCancelDialog } from "imagus-components";

const styles = (theme: DictionaryType): DictionaryType => ({
  ...tableStyles(theme),
  ...buttonStyles(theme),
  ...iconStyles(theme),
  center: {
    textAlign: "center"
  },
  rightMargin: {
    marginRight: "5px"
  }
});

type MatchedInfoClassesType = {
  ...TableStylesClassesType,
  ...ButtonStylesClassesType,
  ...IconStylesClassesType,
  center: DictionaryType,
  rightMargin: DictionaryType
};

type MatchedInfoPropsType = {
  classes: MatchedInfoClassesType,
  setImageError: Function,
  activeItem: { [key: string]: * } | null,
  searchStatus: { [key: string]: number },
  setMessage: Function,
  displayTags: boolean,
  displayProbability: boolean,
  faceSearch: { [key: string]: * },
  enrolMultiple: Function
};

type MatchedInfoStatesType = {
  modal: boolean
};

class MatchedInfo extends Component<
  MatchedInfoPropsType,
  MatchedInfoStatesType
> {
  constructor(props: MatchedInfoPropsType) {
    super(props);
    this.state = {
      modal: false
    };
  }

  switchModalStatus =
    (modalName: string): * =>
    (status: boolean): * => {
      this.setState({ [modalName]: status });
    };

  faceEnrol = () => {
    this.props.setMessage(false, 0);

    let personUid = this.props.activeItem.personUid;
    let enrolment: EnrolmentInputModel = {
      enrolmentId: Date.now(),
      token: this.props.faceSearch.imgToken,
      eyes: this.props.faceSearch.searchedPerson
    };
    this.props.enrolMultiple(
      personUid,
      enrolment,
      this.enrolComplete,
      this.enrolFail
    );
  };

  enrolComplete = (): * => {
    this.props.setMessage(true, 0);
    this.switchModalStatus("modal")(false);
  };

  enrolFail = (): * => {
    this.props.setImageError("The same image has enrolled in other person.");
  };

  render(): * {
    const classes = this.props.classes;
    const activeItem = this.props.activeItem;
    const searchStatus = this.props.searchStatus;

    return (
      <Table className={classNames(classes.appTable, classes.noBorder)}>
        {(activeItem && (
          <TableBody>
            <TableRow>
              <TableCell
                padding="dense"
                className={classNames(classes.blueShine, classes.center)}
                colSpan="2"
              >
                Identified Person Detail
              </TableCell>
              <TableCell
                padding="dense"
                className={classNames(classes.blueShine, classes.center)}
                colSpan="2"
              >
                Search Stats
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell padding="dense" className={classes.border}>
                Name
              </TableCell>
              <TableCell padding="dense" className={classes.normal}>
                {activeItem.personName}
              </TableCell>
              <TableCell padding="dense" className={classes.border}>
                Face Count
              </TableCell>
              <TableCell padding="dense" className={classes.normal}>
                {searchStatus.faceCount}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell padding="dense" className={classes.border}>
                Confidence
              </TableCell>
              <TableCell padding="dense" className={classes.normal}>
                {activeItem.matchQuality}
              </TableCell>
              <TableCell padding="dense" className={classes.border}>
                People Count
              </TableCell>
              <TableCell padding="dense" className={classes.normal}>
                {searchStatus.peopleCount}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell padding="dense" className={classes.border}>
                Distance
              </TableCell>
              <TableCell padding="dense" className={classes.normal}>
                {activeItem.distance.toFixed(2)}
              </TableCell>
              <TableCell padding="dense" className={classes.border}>
                Result Count
              </TableCell>
              <TableCell padding="dense" className={classes.normal}>
                {searchStatus.resultCount}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell padding="dense" className={classes.border}>
                {this.props.displayProbability && "Probability"}
              </TableCell>
              <TableCell padding="dense" className={classes.normal}>
                {this.props.displayProbability &&
                  (parseFloat(activeItem.probability) * 100).toFixed(2) + "%"}
              </TableCell>
              <TableCell padding="dense" className={classes.border}>
                Completion Time(s)
              </TableCell>
              <TableCell padding="dense" className={classes.normal}>
                {searchStatus.seconds.toFixed(2)}
              </TableCell>
            </TableRow>
            {this.props.displayTags && (
              <TableRow>
                <TableCell padding="dense" className={classes.border}>
                  Tags
                </TableCell>
                <TableCell
                  padding="dense"
                  className={classes.normal}
                  colSpan="3"
                >
                  {activeItem.tags.map((tag: *, i: number): * => (
                    <Chip key={i} label={tag} />
                  ))}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell padding="dense" className={classes.border} colSpan="4">
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  className={classes.darkBtn}
                  onClick={(): Function =>
                    this.switchModalStatus("modal")(true)
                  }
                >
                  <PersonAddIcon
                    className={classNames(
                      classes.lightIcon,
                      classes.rightMargin
                    )}
                  />
                  Add to {Stringellipsis(activeItem.personName, 20)}
                </Button>
                <ConfirmCancelDialog
                  open={this.state.modal}
                  handleModalStatus={this.switchModalStatus("modal")}
                  deleteFunction={this.faceEnrol}
                  deleteWord="Enrol"
                  itemName={activeItem.personName}
                  extraWord="this face"
                />
              </TableCell>
            </TableRow>
          </TableBody>
        )) || (
          <TableBody>
            <TableRow>
              <TableCell padding="dense" className={classes.border}>
                No Matched Person
              </TableCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
    );
  }
}

export default withStyles(styles)(MatchedInfo);
